import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RiArrowRightSLine } from "react-icons/ri";
import { getRequest, putRequest } from "../../helpers/api.helper";
import { totalPropertyValueOptions } from "../../utils/constants";

const Step7Edit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { profile } = location.state || {};
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  // Add profile check
  if (!profile) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600 font-semibold">
        ⚠️ Profile data not available.
      </div>
    );
  }

  /** ---------- Fetch Dropdown Data ---------- **/
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest("/common/city");
        //@ts-ignore
        setCity(response);
      } catch (error) {
        console.error(`Error fetching data:`, error);
      }
    };
    fetchData();
  }, []);

  /** ---------- State Management ---------- **/
  const [modalData, setModalData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [city, setCity] = useState([]);

  // Initialize with profile data
  const [selectedOptions, setSelectedOptions] = useState<any>({
    Number_Of_Flats: profile?.numberOfFlats
      ? {
          value: profile.numberOfFlats,
          label: profile.numberOfFlats.toString(),
        }
      : null,
    Flat_Type: profile?.flatType
      ? { value: profile.flatType, label: getFlatTypeLabel(profile.flatType) }
      : null,
    Flat_Value: profile?.flatValue
      ? {
          value: profile.flatValue,
          label: `₹${parseInt(profile.flatValue).toLocaleString("en-IN")}`,
        }
      : null,
    Flat_Location: profile?.flatLocation || [],
    Agriculture_Land: profile?.agricultureLand
      ? {
          value: profile.agricultureLand,
          label: `${profile.agricultureLand} Acres`,
        }
      : null,
    Agriculture_Land_Value: profile?.agricultureLandValue
      ? {
          value: profile.agricultureLandValue,
          label: `₹${parseInt(profile.agricultureLandValue).toLocaleString(
            "en-IN"
          )}`,
        }
      : null,
    Agriculture_Land_Location: profile?.agricultureLandLocation || [],
    Anymore_Properties: profile?.anyMoreProperties
      ? {
          value: profile.anyMoreProperties,
          label: profile.anyMoreProperties === "yes" ? "Yes" : "No",
        }
      : null,
    Total_Properties_Value: profile?.totalPropertiesValue
      ? {
          value: profile.totalPropertiesValue,
          label: profile.totalPropertiesValue,
        }
      : null,
  });

  // Helper function to get flat type label
  function getFlatTypeLabel(value: any) {
    if (value.includes("bhk")) {
      const number = value.replace("bhk", "");
      return `${number} BHK`;
    }
    return value;
  }

  /** ---------- Dropdown Options ---------- **/
  const dropdownOptions: any = {
    Number_Of_Flats: {
      type: "single",
      options: [
        ...Array.from({ length: 10 }, (_, i) => ({
          value: i + 1,
          label: `${i + 1}`,
        })),
        { value: "more", label: "More" },
      ],
    },
    Flat_Type: {
      type: "single",
      options: Array.from({ length: 10 }, (_, i) => ({
        value: `${i + 1}bhk`,
        label: `${i + 1} BHK`,
      })),
    },
    Flat_Value: {
      type: "single",
      options: [
        // 10 Lakhs → 1 Crore
        ...Array.from({ length: 10 }, (_, i) => {
          const amount = (i + 1) * 1000000;
          const label = `₹${(amount / 100000).toFixed(0)} Lakhs`;
          return { value: label, label };
        }),

        // 1 Crore → 10 Crores
        ...Array.from({ length: 10 }, (_, i) => {
          const amount = (i + 1) * 10000000;
          const label = `₹${(amount / 10000000).toFixed(0)} Crores`;
          return { value: label, label };
        }),

        // 10 Crores → 100 Crores
        ...Array.from({ length: 10 }, (_, i) => {
          const amount = (i + 1) * 100000000;
          const label = `₹${(amount / 10000000).toFixed(0)} Crores`;
          return { value: label, label };
        }),
      ],
    },
    Flat_Location: {
      type: "multi",
      options: city.map((c: any) => ({
        value: c.city,
        label: c.city,
      })),
    },
    Agriculture_Land: {
      type: "single",
      options: Array.from({ length: 100 }, (_, i) => ({
        value: i + 1,
        label: `${i + 1} Acres`,
      })),
    },
    Agriculture_Land_Value: {
      type: "single",
      options: [
        { value: 100000, label: "1 Lakh" },
        ...Array.from({ length: 9 }, (_, i) => ({
          value: (i + 2) * 100000,
          label: `${i + 2} Lakhs`,
        })),
        { value: 2000000, label: "20 Lakhs" },
        { value: 3000000, label: "30 Lakhs" },
        { value: 4000000, label: "40 Lakhs" },
        { value: 5000000, label: "50 Lakhs" },
        { value: 6000000, label: "60 Lakhs" },
        { value: 7000000, label: "70 Lakhs" },
        { value: 8000000, label: "80 Lakhs" },
        { value: 9000000, label: "90 Lakhs" },

        // 1 Cr → 5 Cr
        ...Array.from({ length: 5 }, (_, i) => ({
          value: (i + 1) * 10000000,
          label: `${i + 1} Cr above`,
        })),
        // 5 Cr to 500 Cr with 5 Cr increments
        ...Array.from({ length: 99 }, (_, i) => ({
          value: (i + 1) * 50000000 + 50000000, // Starting from 10 Cr (5+5) and incrementing by 5 Cr
          label: `${(i + 1) * 5 + 5} Cr above`,
        })),
      ],
    },
    Agriculture_Land_Location: {
      type: "multi",
      options: city.map((c: any) => ({
        value: c.city,
        label: c.city,
      })),
    },
    Anymore_Properties: {
      type: "single",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    },
    Total_Properties_Value: {
      type: "single",
      options: totalPropertyValueOptions,
    },
  };

  /** ---------- Effects ---------- **/
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!modalData && searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [modalData]);

  useEffect(() => {
    if (modalData) {
      setSearchTerm("");
    }
  }, [modalData]);

  /** ---------- Handlers ---------- **/
  const openModal = (key: any) => {
    setModalData({
      key,
      type: dropdownOptions[key].type,
      options: dropdownOptions[key].options,
    });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleMultiSelectToggle = (value: any) => {
    const currentValues = selectedOptions[modalData.key] || [];
    const updatedValues = currentValues.includes(value)
      ? currentValues.filter((v: any) => v !== value)
      : [...currentValues, value];

    setSelectedOptions((prev: any) => ({
      ...prev,
      [modalData.key]: updatedValues,
    }));
  };

  const handleSingleSelect = (option: any) => {
    setSelectedOptions((prev: any) => ({
      ...prev,
      [modalData.key]: option,
    }));
    closeModal();
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const formDataToSend = {
      numberOfFlats: selectedOptions.Number_Of_Flats?.value || "",
      flatType: selectedOptions.Flat_Type?.value || "",
      flatValue: selectedOptions.Flat_Value?.value || "",
      flatLocation: selectedOptions.Flat_Location || [],
      agricultureLand: selectedOptions.Agriculture_Land?.value || "",
      agricultureLandValue:
        selectedOptions.Agriculture_Land_Value?.value || "",
      agricultureLandLocation: selectedOptions.Agriculture_Land_Location || [],
      anyMoreProperties: selectedOptions.Anymore_Properties?.value || "",
      totalPropertiesValue: selectedOptions.Total_Properties_Value?.value || "",
      step1: 1,
      step2: 1,
      step3: 1,
      step4: 1,
      step5: 1,
      step6: 1,
      step7: 1,
    };
    try {
      const response = await putRequest(
        `/bureau/profile/${profile._id}`,
        formDataToSend
      );
      if (response.success) {
        alert("Details updated successfully!");
        navigate(-1);
      } else {
        // alert(response.message || "")
        alert("Failed to update details.");
      }
    } catch (error) {
      console.error("Error updating details:", error);
      alert("An error occurred while updating details.");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  /** ---------- Helper Functions ---------- **/
  const getDisplayValue = (key: any) => {
    const value = selectedOptions[key];
    const options = dropdownOptions[key]?.options || [];

    if (dropdownOptions[key]?.type === "multi") {
      if (!value || value.length === 0)
        return `Select ${key.replace(/_/g, " ")}`;
      return options
        .filter((opt: any) => value.includes(opt.value))
        .map((opt: any) => opt.label)
        .join(", ");
    } else {
      return value?.label || `Select ${key.replace(/_/g, " ")}`;
    }
  };

  /** ---------- Reusable Modal Component ---------- **/
  const Modal = () => {
    if (!modalData) return null;

    const filteredOptions = modalData.options.filter((option: any) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData.key.replace(/_/g, " ")}
          </h2>

          {/* Search Bar */}
          <input
            ref={searchInputRef}
            type="text"
            autoFocus
            className="w-full px-4 py-2 mb-4 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-200"
            placeholder={`Search ${modalData.key.replace(/_/g, " ")}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Options List */}
          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: any, index: any) => (
                <React.Fragment key={option.value}>
                  {modalData.type === "multi" ? (
                    // Multi-select checkbox
                    <div className="flex items-center space-x-2 py-2">
                      <input
                        type="checkbox"
                        checked={(
                          selectedOptions[modalData.key] || []
                        ).includes(option.value)}
                        onChange={() => handleMultiSelectToggle(option.value)}
                        className="form-checkbox h-4 w-4 text-red-600"
                      />
                      <label
                        className="block text-gray-700 cursor-pointer"
                        onClick={() => handleMultiSelectToggle(option.value)}
                      >
                        {option.label}
                      </label>
                    </div>
                  ) : (
                    // Single-select button
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                      onClick={() => handleSingleSelect(option)}
                    >
                      {option.label}
                    </button>
                  )}
                  {index < filteredOptions.length - 1 && (
                    <hr className="border-gray-200" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No options found
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
            onClick={closeModal}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  /** ---------- Render ---------- **/
  return (
    <div className="profile-edit-page flex justify-center items-center min-h-screen bg-gradient-to-r from-blue-50 to-indigo-100">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="w-full z-50 bg-red-600 fixed top-0 mb-12 p-2 px-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <button className="flex items-center" onClick={handleBackClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-center text-white py-3">
              Step 7 -
            </h1>
            <h5 className="font-semibold text-white">
              Agriculture & Flat Details
            </h5>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 mt-20 px-3 py-4">
          {/* All Fields */}
          {Object.keys(dropdownOptions).map((key) => (
            <div key={key}>
              <label className="block text-red-500 font-medium mb-2 capitalize">
                {key.replace(/_/g, " ")}
              </label>
              <div
                className="flex flex-col w-full border px-3 border-gray-300 py-3 cursor-pointer rounded-lg hover:shadow-lg transition-all"
                onClick={() => openModal(key)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-md font-medium">
                    {getDisplayValue(key)}
                  </span>
                  <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                </div>
              </div>
            </div>
          ))}

          <button
            type="submit"
            className="w-full bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            Update Agriculture & Flat Details
          </button>
        </form>

        {/* Reusable Modal */}
        <Modal />
      </div>
    </div>
  );
};

export default Step7Edit;
